import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ShipInfoListTable from "../components/ShipInfoListTable";
import { getErrorMessage, getPageDescription, getSuccessMessage, getUiInterface } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { useShipInfo } from "../../../apis/ShipInfo/ShipInfoProvider";
import { getShipInfo } from "../../../services/shipinfoService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import Paginacao from "../../../types/paginacao";
import { ShipInfo } from "../../../types/shipinfo";
import { FaEdit, FaInfoCircle, FaBan } from "react-icons/fa";
import ShippingInfoModal, { ShippingData } from '../components/ShippingInfoModal';
import CancelConfirmationModal from "./CancelConfirmationModal";

// A API so aplica o filtro de release quando inicio E fim estao presentes
// (ReleaseDAO.FindByFilter exige os dois, mesmo padrao do filtro de PU em CargoDAO).
// Para expressar "release >= 01/01/2021" sem limite superior, o fim usa uma
// data bem no futuro - nunca alcancada na pratica.
const RELEASED_FILTRO_DATA_MINIMA = '2021-01-01';
const RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE = '2099-12-31';

const ShipInfoListView: React.FC = () => {
    const { shipInfos, apiResponse, fetchShipInfos, markShipInfoAsShipped, cancelShipping } = useShipInfo();
    const navigate = useNavigate();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelId, setCancelId] = useState<number | null>(null);
    const [selectedData, setSelectedData] = useState<ShippingData | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    
    const handleCloseModal = () => setShowModal(false);

    const handleInfoClick = (id: string) => {
        const selected = (shipInfos ?? []).find(info => info.id === id);

        if (selected) {
            setSelectedId(Number(id));
            setSelectedData({
                mawb: selected.master || "",
                hawb: selected.house || "",
                shipDate: selected.shipDate?.split("T")[0] || "",
                arrivalDate: selected.arrival?.split("T")[0] || "",
            });
            setShowModal(true);
        }
    };

    const handleCancelClick = (id: string) => {
        setCancelId(Number(id));
        setShowCancelModal(true);
    };

    const handleConfirmCancel = async () => {
        if (cancelId !== null) {
            try {
                await cancelShipping(cancelId);
                setAlert({ message: getSuccessMessage("cancelShipping", { id: cancelId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("cancelShippingError"), variant: "danger" });
            } finally {
                setShowCancelModal(false);
                setCancelId(null);
            }
        }
    };

    const handleSubmit = async (data: ShippingData) => {
        if (selectedId !== null) {
            try {
                await markShipInfoAsShipped(selectedId, data);
                setAlert({ message: getSuccessMessage("editShipInfo", { id: selectedId }), variant: "success" });
                fetchShipInfos(paginacao, searchParams);
            } catch (error) {
                setAlert({ message: getErrorMessage("editShipInfoError"), variant: "danger" });
            }
        } else {
            setAlert({ message: getErrorMessage("editNoId"), variant: "warning" });
        }

        setShowModal(false); 
    };

    const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Ligado por padrao: a tela sempre abre mostrando so processos recentes.
    const [filtrarReleasedApartir2021, setFiltrarReleasedApartir2021] = useState(true);

    const [searchParams, setSearchParams] = useState({
        shipped: true,
        campo: "dawb",
        valor: "",
        shippedBy: "",
        destination: "",
        freightType: "",
        releasedBy: "",
        consignee: "",
        to: "",
        releaseDate: "",
        master: "",
        house: "",
        shipDate: "",
        arrival: "",
        releasedInicio: RELEASED_FILTRO_DATA_MINIMA,
        releasedFim: RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE,
    });

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 40,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 40
    } as Paginacao);

    const [exportando, setExportando] = useState(false);

    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

   const filterOptions = [
        { value: "dawb", label: "DAWB" },
        { value: "description", label: "Description" },
        { value: "ponumber", label: "PO Number" },
        { value: "wrnumber", label: "WR Number" },
        { value: "supplier", label: "Supplier" },
        { value: "invoicenumber", label: "Invoice Number" }
    ];

   useEffect(() => {
        const newPaginacao: Paginacao = {
            ...paginacao,
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize,
            sort: sortModel[0]?.field,
            order: sortModel[0]?.sort ?? undefined,
        };
        setPaginacao(newPaginacao);
        fetchShipInfos(newPaginacao, searchParams);
    }, [paginationModel, sortModel]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const valor = (e.currentTarget as any)[1].value;
        const updatedSearchParams = { ...searchParams, valor };

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams)
            .filter(([_, value]) => value !== "")
        );

        fetchShipInfos(paginacao, filters);
    };

    /**
     * Liga/desliga o filtro de release sem exigir clique em "Search" - e um
     * checkbox, a resposta precisa ser imediata.
     */
    const handleToggleFiltrarReleased = (checked: boolean) => {
        setFiltrarReleasedApartir2021(checked);

        const updatedSearchParams = {
            ...searchParams,
            releasedInicio: checked ? RELEASED_FILTRO_DATA_MINIMA : '',
            releasedFim: checked ? RELEASED_FILTRO_DATA_MAXIMA_SEM_LIMITE : '',
        };
        setSearchParams(updatedSearchParams);

        const filters = Object.fromEntries(
            Object.entries(updatedSearchParams).filter(([, value]) => value !== "")
        );
        fetchShipInfos(paginacao, filters);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const navItems: NavBarItem[] = [
        { label: "Home", route: "/", iconName: "house-door" },
        { label: "Release List", route: "/Shipinfo", iconName: "list" }
    ];

    const safeValueGetter = (field: keyof ShipInfo) => (params: GridRenderCellParams<ShipInfo>) => {
        const value = params?.row?.[field];
        return value ? String(value) : "-";
    };

    const columns: GridColDef<ShipInfo>[] = [
    {
        field: "actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
        <div className="d-flex justify-content-center mt-1">
            <Button
            variant="link"
            size="lg"
            onClick={() => handleCancelClick(params.row.id)}
            className="p-1"
            >
            <FaBan className="text-danger" />
            </Button>
            <Button
            variant="link"
            size="lg"
            onClick={() => handleInfoClick(params.row.id)}
            className="p-1"
            >
            <FaEdit className="text-info" />
            </Button>
            <Button
            variant="link"
            size="lg"
            onClick={() => handleNavigation(`/shipinfo/${params.row.id}`)}
            className="p-1"
            >
            <FaInfoCircle className="text-primary" />
            </Button>
        </div>
        ),
    },
    {
        field: "shippedBy",
        headerName: "Shipped By",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.shippedBy || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.shippedBy || "-"}</span>
        ),
      },
      {
        field: "destination",
        headerName: "Destination",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.destination?.description || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.destination?.description || "-"}</span>
        ),
      },
      {
        field: "freightType",
        headerName: "Freight Type",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.freightType?.description || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.freightType?.description || "-"}</span>
        ),
      },
      {
        field: "releasedBy",
        headerName: "Released by",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.releasedBy?.fullName || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.releasedBy?.fullName || "-"}</span>
        ),
      },
      {
        field: "consignee",
        headerName: "Consignee",
        width: 300,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.consignee?.name || "-",
        renderCell: (params: GridRenderCellParams<ShipInfo>) => (
          <span style={{ color: "black" }}>{params?.row?.consignee?.name || "-"}</span>
        ),
      },
      {
        field: "to",
        headerName: "To",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.to?.name || "-",
        renderCell: (params) => {
          if (!params?.row?.to?.name) return <span>-</span>;
          return <span style={{ color: "black" }}>{params.row.to.name}</span>;
        },
      },
      {
        field: "released",
        headerName: "Release date",
        width: 180,
        valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
          params?.row?.released ?? "-",
        valueFormatter: (params: any) => {
          const dateStr = params?.value;
          if (!dateStr) return "-";
          try {
            const dateValue = new Date(dateStr);
            return dateValue.toLocaleDateString();
          } catch {
            return "-";
          }
        },
        renderCell: (params: GridRenderCellParams<ShipInfo>) => {
          if (!params?.row?.released) return <span>-</span>;
          try {
            const dateValue = new Date(params.row.released);
            return <span style={{ color: "black" }}>{dateValue.toLocaleDateString()}</span>;
          } catch {
            return <span>-</span>;
          }
        },
      },
        {
            field: "master",
            headerName: "Master",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.master || "-",
            renderCell: (params) => {
                if (!params?.row?.master) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.master}</span>;
            },
        },
        {
            field: "house",
            headerName: "House",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.house || "-",
            renderCell: (params) => {
                if (!params?.row?.house) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.house}</span>;
            },
        },
        {
            field: "shipDate",
            headerName: "Ship Date",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.shipDate || "-",
            renderCell: (params) => {
                if (!params?.row?.shipDate) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.shipDate}</span>;
            },
        },
        {
            field: "arrival",
            headerName: "Arrival",
            width: 160,
            valueGetter: (params: GridRenderCellParams<ShipInfo>) =>
                params?.row?.arrival || "-",
            renderCell: (params) => {
                if (!params?.row?.arrival) return <span>-</span>;
                return <span style={{ color: "black" }}>{params.row.arrival}</span>;
            },
        }
    ];

    const handleNavigation = (route: string) => {
        navigate(route);
    };

    /**
     * Exporta para Excel todos os itens que batem com o filtro atual - nao so a
     * pagina visivel. Busca direto do servico, sem tocar no estado do ShipInfoProvider
     * que alimenta a grid.
     */
    const handleExportarExcel = async () => {
        setExportando(true);

        try {
            const filtros = Object.fromEntries(
                Object.entries(searchParams).filter(([, value]) => value !== "")
            );
            const resposta = await getShipInfo(
                { pageNum: 0, quantityPerPage: 100000 } as Paginacao,
                filtros
            );

            const linhas = (resposta?.data?.values ?? []).map((row: any) => ({
                'Shipped By': row?.shippedBy || '-',
                'Destination': row?.destination?.description || '-',
                'Freight Type': row?.freightType?.description || '-',
                'Released by': row?.releasedBy?.fullName || '-',
                'Consignee': row?.consignee?.name || '-',
                'To': row?.to?.name || '-',
                'Release date': row?.released ? new Date(row.released).toLocaleDateString() : '-',
                'Master': row?.master || '-',
                'House': row?.house || '-',
                'Ship Date': row?.shipDate || '-',
                'Arrival': row?.arrival || '-',
            }));

            if (!(await exportarParaExcel(linhas, 'ship_info_shipped'))) {
                setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
            }
        } catch (error) {
            setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
        } finally {
            setExportando(false);
        }
    };

    return (
         <Container fluid style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}>
            <ShippingInfoModal
                show={showModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                initialData={selectedData ?? undefined}
            />

            <CancelConfirmationModal
                show={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleConfirmCancel}
                shipId={cancelId}
            />
            
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getUiInterface("search")}</h2>
            <div className="mb-3"></div>
            
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-4 p-3 bg-light rounded">
                <h5 className="mb-3">{getUiInterface("searchFor")}</h5>
                
                <Form onSubmit={handleSearch}>
                <Row className="mb-3">
                    <Col md={3}>
                        <Form.Group controlId="campo">
                            <Form.Select
                                name="campo"
                                value={searchParams.campo}
                                onChange={handleInputChange}
                            >
                                {filterOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="valor">
                            <Form.Control
                                type="text"
                                name="valor"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <Button variant="primary" type="submit">
                                Search
                            </Button>
                            <Form.Check
                                type="checkbox"
                                id="filtro-released-apartir-2021"
                                label={getUiInterface('filterReleasedFrom2021')}
                                checked={filtrarReleasedApartir2021}
                                onChange={(e) => handleToggleFiltrarReleased(e.target.checked)}
                            />
                        </div>
                    </Col>
                </Row>
            </Form>
            </div>

            <h3 className="mb-2">{getPageDescription("releaseList")}</h3>
            <div className="d-flex justify-content-end mb-2">
                <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
                <ShipInfoListTable
                    rows={Array.isArray(shipInfos) ? shipInfos : []}
                    columns={columns}
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                    apiResponse={apiResponse}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </div>
        </Container>
    );
};

export default ShipInfoListView;